import React from "react"
import { Modal, Form, Col, Row, Button, Container, InputGroup, Table, Spinner } from "react-bootstrap"
import { FaAngleDoubleDown, FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleDoubleUp, FaAngleDown, FaAngleUp, FaChevronLeft, FaChevronRight } from "react-icons/fa"
import { toast } from "react-toastify"
import { concat, of, Subject, Subscription } from "rxjs"
import { catchError, filter, map, switchMap } from "rxjs/operators"
import { connect } from "react-redux"
import { isUser } from "../../authentication/isAdminRole"
import { AppState } from "../../AppState"
import ActionButton from "../ActionButton"
import UserInformation from "../../api/Administration/Model/UserInformation"
import { UserNotificationInformationObservable } from "../../api/Administration/UserInformationGet"
import OrderSecurityOverrideData from "../../api/Administration/Model/OrderSecurityOverrideData"
import OrderSecurityOverrideRecord from "../../api/Administration/Model/OrderSecurityOverrideRecord"
import OrderSecurityOverrideGet from "../../api/Administration/OrderSecurityOverrideGet"
import OrderSecurityOverrideUpdate from "../../api/Administration/OrderSecurityOverrideUpdate"
import RoleOrderSecurityCheck from "../../api/Roles/RoleOrderSecurityCheck"
import FormatDate from "../../formatters/FormatDate"
import SetDatePeriod, { DatePeriodItem } from "./SetDatePeriod"
import { APIError } from "../../api/ObservableFromFetch"
import FormatError from "../../formatters/FormatError"

interface OrderSecurityOverrideProps {
  show: boolean
  userInfo?: UserInformation
  onClose: () => void
}

interface OrderSecurityOverrideAuthProps {
  isUser?: boolean
}

interface OrderSecurityOverrideRecordExt extends OrderSecurityOverrideRecord {
  id: string
}

interface OrderSecurityOverrideState {
  userInfo?: UserInformation
  loginName: string
  loading: boolean
  saving: boolean
  roleChecking: boolean
  loadingOverrides: boolean
  showSetDates: boolean
  whoCanViewMyOrders: OrderSecurityOverrideRecordExt[]
  whoCannotViewMyOrders: OrderSecurityOverrideRecordExt[]
  selectionLeft: string[]
  selectionRight: string[]
}

export class OrderSecurityOverride extends React.PureComponent<OrderSecurityOverrideProps & OrderSecurityOverrideAuthProps, OrderSecurityOverrideState> {
  state: OrderSecurityOverrideState = {
    loginName: "",
    loading: false,
    saving: false,
    roleChecking: false,
    loadingOverrides: false,
    showSetDates: false,
    whoCanViewMyOrders: [],
    whoCannotViewMyOrders: [],
    selectionLeft: [],
    selectionRight: [],
  }

  loadUserSubscription?: Subscription
  roleSecurityCheckSubscription?: Subscription
  loadOrderSecurityOverrideSubscription?: Subscription
  saveSubscription?: Subscription

  loginName$ = new Subject<string>()
  roleCheck$ = new Subject<UserInformation | undefined>()
  override$ = new Subject<UserInformation | undefined>()

  componentDidMount() {
    this.loadUserSubscription = this.loginName$.pipe(
      map(name => name.trim()),
      switchMap(name => concat(
        of({ loading: true }),
        UserNotificationInformationObservable(name, true).pipe(
          map(result => ({ loading: false, result: result })),
          catchError((error) => of({ loading: false, errorMessage: FormatError(error) }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: UserInformation[], errorMessage?: string }) => {
        this.setState({ loading: o.loading, whoCanViewMyOrders: [], whoCannotViewMyOrders: [], selectionLeft: [], selectionRight: [], userInfo: undefined })
        if (o.errorMessage) {
          toast.error(o.errorMessage)
        } else {
          const userInfo = o.result && o.result.length > 0 ? o.result[0] : undefined
          this.roleCheck$.next(userInfo)
        }
      },
      (error) => this.handleAPIError(error, { loading: false, userInfo: undefined })
    )
    this.roleSecurityCheckSubscription = this.roleCheck$.pipe(
      filter(userInfo => userInfo !== undefined),
      switchMap(userInfo => concat(
        of({ loading: true, result: false }),
        RoleOrderSecurityCheck(userInfo!.RoleID).pipe(
          map(result => ({ loading: false, result: result, userInfo: userInfo })),
          catchError((error) => of({ loading: false, result: false, errorMessage: FormatError(error) }))
        )))
    ).subscribe(
      (o: { loading: boolean, result: boolean, userInfo?: UserInformation, errorMessage?: string }) => {
        this.setState({ roleChecking: o.loading })
        if (o.errorMessage) {
          toast.error(o.errorMessage)
        } else {
          this.override$.next(o.userInfo)
        }
      },
      (error) => this.handleAPIError(error, { roleChecking: false })
    )
    this.loadOrderSecurityOverrideSubscription = this.override$.pipe(
      filter(userInfo => userInfo !== undefined),
      switchMap(userInfo => concat(
        of({ loading: true }),
        OrderSecurityOverrideGet(userInfo!.RoleID, userInfo!.ApplicationUserID).pipe(
          map(result => ({ loading: false, result: result, userInfo: userInfo })),
          catchError((error) => of({ loading: false, errorMessage: FormatError(error) }))
        )))
    ).subscribe(
      (o: { loading: boolean, result?: OrderSecurityOverrideData, userInfo?: UserInformation, errorMessage?: string }) => {
        this.setState({
          loadingOverrides: o.loading,
          userInfo: o.userInfo,
          loginName: o.userInfo ? o.userInfo.UserName : this.state.loginName,
          whoCanViewMyOrders: o.result?.WhoCanViewMyOrders?.map(item => ({ ...item, id: Math.random().toString() })) || [],
          whoCannotViewMyOrders: o.result?.WhoCannotViewMyOrders?.map(item => ({ ...item, id: Math.random().toString() })) || [],
          selectionLeft: [],
          selectionRight: []
        })
        o.errorMessage && toast.error(o.errorMessage)
      },
      (error) => this.handleAPIError(error, { loadingOverrides: false })
    )
    if (this.props.show && !!this.props.userInfo) {
      this.roleCheck$.next(this.props.userInfo)
    }
  }

  componentWillUnmount() {
    this.loadUserSubscription && this.loadUserSubscription.unsubscribe()
    this.roleSecurityCheckSubscription && this.roleSecurityCheckSubscription.unsubscribe()
    this.loadOrderSecurityOverrideSubscription && this.loadOrderSecurityOverrideSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: OrderSecurityOverrideProps) {
    if (this.props.show && !prevProps.show) {
      if (!!this.props.userInfo) {
        this.roleCheck$.next(this.props.userInfo)
      }
    }
  }

  handleLoginNameChange = (e: React.ChangeEvent<HTMLInputElement>) => this.setState({ loginName: e.target.value })

  move(dir: "ltr" | "rtl", all?: boolean) {
    if (dir === "ltr") {
      const ids = !!all ? this.state.whoCannotViewMyOrders.map(item => item.id) : this.state.selectionLeft
      this.setState({
        selectionLeft: [],
        whoCannotViewMyOrders: this.state.whoCannotViewMyOrders.filter(item => !ids.includes(item.id)),
        whoCanViewMyOrders: [...this.state.whoCanViewMyOrders, ...this.state.whoCannotViewMyOrders.filter(item => ids.includes(item.id))]
      })
    } else {
      const ids = !!all ? this.state.whoCanViewMyOrders.map(item => item.id) : this.state.selectionRight
      this.setState({
        selectionRight: [],
        whoCannotViewMyOrders: [...this.state.whoCannotViewMyOrders, ...this.state.whoCanViewMyOrders.filter(item => ids.includes(item.id))],
        whoCanViewMyOrders: this.state.whoCanViewMyOrders.filter(item => !ids.includes(item.id))
      })
    }
  }

  handleUpdate = () => {
    const { userInfo, whoCanViewMyOrders } = this.state
    if (!userInfo) {
      return
    }
    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.saveSubscription = OrderSecurityOverrideUpdate(
      userInfo?.RoleID,
      userInfo?.MockApplicationUserID,
      whoCanViewMyOrders
    ).subscribe(
      data => {
        this.setState({ saving: false })
        toast.success(data)
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  handleRightItemClick = (id: string) => {
    if (this.state.selectionRight.includes(id)) {
      this.setState({ selectionRight: this.state.selectionRight.filter(item => item !== id) })
    } else {
      this.setState({ selectionRight: [...this.state.selectionRight, id] })
    }
  }

  handleSetDatesUpdate = (datePeriodItems: DatePeriodItem[]) => {
    const { whoCanViewMyOrders } = this.state
    whoCanViewMyOrders.forEach(item => {
      const datePeriodItem = datePeriodItems.find(datePeriodItem => datePeriodItem.id === item.id)
      if (datePeriodItem) {
        item.DateFrom = datePeriodItem.from
        item.DateTo = datePeriodItem.to
      }
    })
    this.setState({ showSetDates: false, whoCanViewMyOrders: whoCanViewMyOrders.map(item => ({ ...item })) })
  }

  render() {
    const { userInfo, loginName, loading, saving, roleChecking, loadingOverrides,
      whoCanViewMyOrders, whoCannotViewMyOrders, selectionLeft, selectionRight } = this.state
    const { isUser } = this.props

    const canFinish = !!whoCanViewMyOrders.length && whoCanViewMyOrders.every(item => !!item.DateFrom && !!item.DateTo)
    return (<>
      <Modal backdrop="static" show={this.props.show} size="xl">
        <Modal.Header>
          <Modal.Title className="text-uppercase">Order View Security Override</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-0">
          <Container fluid className="px-4">
            <Row>
              <Col xl={5} className="pl-1 pb-1 pb-lg-0">
                <InputGroup className="label-prepend">
                  <InputGroup.Prepend>
                    <InputGroup.Text>User's Email</InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control type="text" value={loginName}  disabled={isUser} onChange={this.handleLoginNameChange} />
                  <InputGroup.Append>
                    {!isUser && <ActionButton variant="secondary"
                      disabled={loginName.trim().length === 0}
                      onClick={() => this.loginName$.next(this.state.loginName)}
                      spin={loading || roleChecking || loadingOverrides}
                      disableOnSpin>Retrieve</ActionButton>}
                  </InputGroup.Append>
                </InputGroup>
              </Col>
              <Col className="d-none d-xl-block"></Col>
              <Col xl={6} className="pl-1">
                <InputGroup className="label-prepend">
                  <InputGroup.Prepend>
                    <InputGroup.Text>Role</InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control type="text" defaultValue={userInfo?.RoleDescription} readOnly className="bg-white" />
                </InputGroup>
              </Col>
            </Row>
          </Container>
          <hr />
          {isUser && (loading || roleChecking || loadingOverrides) && <Container fluid><Row><Col className="text-center"><Spinner animation="border" variant="secondary" /></Col></Row></Container>}
          <Form className="px-4" hidden={isUser && (loading || roleChecking || loadingOverrides)}>
            <Row className="d-none d-lg-flex">
              <Col lg={4}>Who cannot view my Orders</Col>
              <Col lg={1} className="px-0"></Col>
              <Col lg={7} className="pl-lg-0">Who can view my Orders</Col>
            </Row>
            <Row>
              <Col className="d-lg-none">Who cannot view my Orders</Col>
              <Col lg={4} className="pr-lg-0">
                <Form.Control as="select" multiple className="grey-selection" style={{ height: "100%", overflowY: "auto" }}
                  disabled={false}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { this.setState({ selectionLeft: Array.from(e.target.selectedOptions, option => option.value) }) }}>
                  {whoCannotViewMyOrders.map(item => <option key={item.id} value={item.id}>{item.UserName}</option>)}
                </Form.Control>
              </Col>
              <Col lg={1} className="px-0">
                <Container fluid>
                  <Row>
                    <Col className="px-1 text-center">
                      <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!whoCannotViewMyOrders.length} onClick={() => this.move("ltr", true)}>
                        <FaAngleDoubleRight className="d-none d-lg-inline" />
                        <FaAngleDoubleDown className="d-lg-none" />
                      </Button>
                    </Col>
                    <Col className="px-1 text-center">
                      <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!selectionLeft.length} onClick={() => this.move("ltr")}>
                        <FaChevronRight className="d-none d-lg-inline" />
                        <FaAngleDown className="d-lg-none" />
                      </Button>
                    </Col>
                    <Col className="px-1 text-center">
                      <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!selectionRight.length} onClick={() => this.move("rtl")}>
                        <FaChevronLeft className="d-none d-lg-inline" />
                        <FaAngleUp className="d-lg-none" />
                      </Button>
                    </Col>
                    <Col className="px-1 text-center">
                      <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!whoCanViewMyOrders.length} onClick={() => this.move("rtl", true)}>
                        <FaAngleDoubleLeft className="d-none d-lg-inline" />
                        <FaAngleDoubleUp className="d-lg-none" />
                      </Button>
                    </Col>
                  </Row>
                </Container>
              </Col>
              <Col className="d-lg-none">Who can view my Orders</Col>
              <Col lg={7} className="pl-lg-0">
                <Table className="table-sm dark" borderless responsive="bordered-h100p">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>From</th>
                      <th>To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {whoCanViewMyOrders.map(item => <tr key={item.id} className={selectionRight.includes(item.id) ? "selected" : ""} onClick={() => this.handleRightItemClick(item.id)}>
                      <td>{item.UserName}</td>
                      <td>{FormatDate(item.DateFrom)}</td>
                      <td>{FormatDate(item.DateTo)}</td>
                    </tr>)}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.props.onClose}>Cancel</Button>
          <Button onClick={() => this.setState({ showSetDates: true })} disabled={!whoCanViewMyOrders.length} className="ml-1">Set Dates</Button>
          <ActionButton onClick={this.handleUpdate} disabled={!canFinish} className="ml-1" spin={saving} disableOnSpin>Finish</ActionButton>
        </Modal.Footer>
      </Modal>
      <SetDatePeriod show={this.state.showSetDates}
        items={whoCanViewMyOrders.map(item => ({ id: item.id, name: item.UserName, from: item.DateFrom, to: item.DateTo }))}
        onCancel={() => this.setState({ showSetDates: false })}
        onUpdate={this.handleSetDatesUpdate} />
    </>)
  }
}

function mapStateToProps(state: AppState): OrderSecurityOverrideAuthProps {
  return {
    isUser: isUser(state)
  }
}

const ConnectedOrderSecurityOverride = connect(mapStateToProps, null)(OrderSecurityOverride)
export default ConnectedOrderSecurityOverride